import React, { useState, useMemo, useRef, useEffect } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  Column,
  Button,
  MasterDetail,
  Editing,
  HeaderFilter,
  Scrolling,
  Toolbar,
  SearchPanel,
  Popup as GridPopup,
  Form,
  Item,
  Label,
  StateStoring,
} from "devextreme-react/data-grid";
import { Button as MyButton } from "devextreme-react/button";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal/index.js";
import { apiBaseURL } from "../../Utils/ProjectConstants.js";
import useFetch from "../../Hooks/useFetch.js";
import {
  checkRoles,
  getUserId,
  getUsersWorkCenter,
} from "../../Utils/helper.js";
import ActionNotifications from "../../Components/ActionNotifications/index.js";
import ErrorMessage from "../../Components/ErrorMessage/index.js";
import DetailTemplate from "./MasterDetail.js";
import Popup from "./poPagePopup.js";
import {
  handledDateRender,
  statusRender,
  urgencyRender,
  purchaseTypes,
  deadlineTypes,
  statusValues,
  booleanTypes,
  booleanRender,
} from "./variables.js";
import "./styles.css";
import useGetAllApprovedSuppliersList from "../../Hooks/useGET/useGetAllApprovedVendorList.js";

const deadlineTypeOptions = {
    items: deadlineTypes,
    valueExpr: "value",
    displayExpr: "text",
    placeholder: "Select...",
  },
  deadlineDateOptions = {
    width: "100%",
    type: "date",
    displayFormat: "shortdate",
    showClearButton: true,
    useMaskBehavior: true,
    placeholder: "mm/dd/yyyy",
  },
  textAreaOptions = {
    height: 90,
  },
  //productQuantityOptions = {
  //    format: "#"
  //},
  //productCostOptions = {
  //    format: "$#.##",
  //    placeholder: "$"
  //},
  purchaseTypeOptions = {
    items: purchaseTypes,
    valueExpr: "id",
    displayExpr: "value",
    placeholder: "Select...",
  },
  totalOptions = { format: "$#.##" };

export default function POPage() {
  const workcenter = getUsersWorkCenter()?.name,
    isUserKristen = false,
    //isUserKristen = getUserId() === "d4ec0560-d7b0-4385-81a3-ef5ac53df4da",
    navigate = useNavigate(),
    fetch = useFetch(navigate),
    notifyResponse = useRef([]),
    [formData, setFormData] = useState({}),
    [showPopup, setPopup] = useState(""),
    filteredValues = useRef(isUserKristen ? [0, 1, 2, 3, 4, 8] : null),
    [selectedPO, setSelectedPO] = useState({}),
    [quoteId, setQuoteId] = useState(0),
    [showSpinner, setShowSpinner] = useState(false),
    { data, mutate, error } = useSWR(`${apiBaseURL}/api/PurchaseOrders`),
    requests = data?.data,
    { data: quoteData } = useSWR(
      quoteId === 0
        ? null
        : `${apiBaseURL}/api/attachment?purchaseOrderId=${quoteId}`,
    ),
    { data: locationData, error: locationError } = useSWR(
      `${apiBaseURL}/api/DeliveryLocation`,
    ),
    locations = locationData?.data,
    { data: userData, error: userError } = useSWR(
      workcenter === "Operations" ? `${apiBaseURL}/api/users/all` : null,
    ),
    { data: typeData } = useSWR(`${apiBaseURL}/api/Project/BillingCodes`),
    billingCodes = useMemo(
      () =>
        typeData?.data.map((x) => ({
          label: `${x.billingCode} - ${x.description}`,
          value: x.billingCode,
          projectId: x.projectId,
        })),
      [typeData],
    ),
    { allSuppliers } = useGetAllApprovedSuppliersList();
  const locationsOptions = useMemo(
      () => ({
        items: locations,
        valueExpr: "id",
        displayExpr: "location",
        searchEnabled: true,
        placeholder: "Type or Select...",
      }),
      [locations],
    ),
    supplierOption = useMemo(
      () => ({
        items: allSuppliers,
        valueExpr: "id",
        displayExpr: "label",
        searchEnabled: true,
        placeholder: "Type or Select...",
      }),
      [allSuppliers],
    ),
    billingCodeOptions = useMemo(
      () => ({
        items: billingCodes,
        valueExpr: "value",
        displayExpr: "label",
        searchEnabled: true,
        placeholder: "Type or Select...",
      }),
      [billingCodes],
    ),
    poStatusOptions = useMemo(
      () => ({
        items: statusValues,
        valueExpr: "value",
        displayExpr: "text",
        searchEnabled: true,
        placeholder: "Type or Select...",
      }),
      [],
    );

  useEffect(() => {
    if (quoteData?.data?.length > 0) {
      quoteData?.data.forEach((attachment) => {
        const url = `https://atlasboard.wsu-idp.org/backend${attachment.url}`;
        window.open(url);
      });
      setQuoteId(0);
    }
  }, [quoteData]);

  const fetchSuccessful = (response) => {
    notifyResponse.current = response;
    return (
      response?.success &&
      response?.data?.statusCode &&
      response?.success !== "Invalid Model"
    );
  };

  const onRowRemoved = async (e) => {
    let response = await fetch.delete(
      `${apiBaseURL}/api/PurchaseOrders?orderId=${e.key}`,
    );
    if (fetchSuccessful(response)) {
      mutate();
    }
  };

  const onRemoveHold = async (e) => {
    //console.log('remove hold', e);
    const body = {
      id: e.id,
      userId: e.requestor?.id,
      vendorName: e.vendorName,
      vendorContact: e.vendorContact,
      vendorEmail: e.vendorEmail,
      vendorAdress: e.vendorAddress,
      deadlineType: e.deadlineType,
      deadlineDate: e.deadlineDate,
      urgencyJustification: e.urgencyJustification,
      poAdditionalEmails: e.poAdditionalEmails,
      additionalInfo: e.additionalInfo,
      shippingLocationId: e.shippingLocation.id,
      shippingAttn: e.shippingAttn,
      provideShippingAccountOnPO: e.provideShippingAccountOnPO,
      isAS9100: e.isAS9100,
      vendor: e.vendor,
      purchaseType: e.purchaseType,
      isInstallationRequired: e.isInstallationRequired,
      installationRequirements: e.installationRequirements,
      billingCode: e.billingCode,
      otherBilling: e.otherBilling,
      poStatus: 2,
    };
    const response = await fetch.put(`${apiBaseURL}/api/PurchaseOrders`, body);
    if (fetchSuccessful(response)) {
      mutate();
    }
  };

  const onEditingStart = (e) => {
    setFormData({
      id: e.data.id,
      requestorContactNumber: e.data.requestorContactNumber,
      userId: getUserId(),
      isAS9100: e.data.isAS9100,
      approvedVendorId: e.data.vendor?.id ?? null,
      vendorName: e.data.vendorName,
      vendorContact: e.data.vendorContact,
      vendorEmail: e.data.vendorEmail,
      vendorAddress: e.data.vendorAddress,
      deadlineType: e.data.deadlineType,
      deadlineDate: e.data.deadlineDate,
      urgencyJustification: e.data.urgencyJustification,
      poAdditionalEmails: e.data.poAdditionalEmails,
      additionalInfo: e.data.additionalInfo,
      shippingLocationId: e.data.shippingLocation.id,
      shippingAttn: e.data.shippingAttn,
      provideShippingAccountOnPO: e.data.provideShippingAccountOnPO,
      purchaseType: e.data.purchaseType,
      isInstallationRequired: e.data.isInstallationRequired,
      installationRequirements: e.data.installationRequirements,
      billingCode: e.data.billingCode,
      otherBilling: e.data.otherBilling,
      poStatus: e.data.poStatus,
    });
  };
  const onFormSave = async (e) => {
    setShowSpinner(true);

    const changes = e.changes?.[0]?.data ?? {};

    if ("shippingLocation.id" in changes) {
      changes.shippingLocationId = changes["shippingLocation.id"];
      delete changes["shippingLocation.id"];
    }

    const body = { ...formData, ...changes };
    if (!body.isAS9100) {
      body.approvedVendorId = null;
    }

    const response = await fetch.put(`${apiBaseURL}/api/PurchaseOrders`, body);

    if (fetchSuccessful(response)) {
      mutate();
      setPopup("");
    }
    setShowSpinner(false);

    e.cancel = true;
  };

  //hold filtering for po page
  const PO_GRID_STATE_KEY = "purchaseOrderGridState";
  useEffect(() => {
    const navigationEntry = performance.getEntriesByType("navigation")[0];
    if (navigationEntry?.type === "reload") {
      sessionStorage.removeItem(PO_GRID_STATE_KEY);
    }
  });

  const renderGrid = () => {
    if (data && locationData) {
      return (
        <>
          <DataGrid
            dataSource={requests}
            keyExpr="id"
            rowAlternationEnabled={false}
            showBorders={true}
            height="80vh"
            onSaving={onFormSave}
            allowColumnResizing={true}
            repaintChangesOnly={true}
            columnAutoWidth={true}
            onRowRemoved={onRowRemoved}
            onRowPrepared={onRowPrepared}
            wordWrapEnabled={true}
            onEditingStart={onEditingStart}
          >
            <SearchPanel
              visible={true}
              maxWidth={275}
              searchVisibleColumnsOnly={true}
              placeholder="Search..."
            />
            <StateStoring
              enabled={true}
              type="sessionStorage"
              storageKey="PO_GRID_STATE_KEY"
            />
            <HeaderFilter visible={true} />
            <Toolbar>
              <Item
                location="before"
                locateInMenu="never"
                render={() => (
                  <div className="misc-costs-title">
                    <h1 className="white">PO Requests</h1>
                  </div>
                )}
              />

              <Item>
                <MyButton
                  name="addRowButton"
                  text="New Purchase Order Request"
                  type="normal"
                  stylingMode="contained"
                  icon="add"
                  onClick={() => navigate(`/purchaseOrders/newPO`)}
                />
              </Item>
              <Item name="searchPanel" />
            </Toolbar>
            <Scrolling mode="virtual" />
            <Editing allowAdding allowUpdating allowDeleting mode="popup">
              <GridPopup
                title="Order Info"
                showTitle={true}
                width="50%"
                height="80%"
              />
              <Form colCount={1} formData={formData}>
                <Item dataField="requestorContactNumber" />
                <Item
                  itemType="group"
                  caption="Vendor Information"
                  colCount={2}
                >
                  <Item
                    dataField="isAS9100"
                    editorType="dxCheckBox"
                    editorOptions={{
                      text: "Requires AS9100 compliance?",
                      elementAttr: {
                        class: "checkbox",
                      },
                    }}
                  >
                    <Label text={" "} />
                  </Item>
                  <Item
                    dataField="approvedVendorId"
                    visible={false}
                    editorType="dxSelectBox"
                    editorOptions={{
                      items: allSuppliers,
                      valueExpr: "id",
                      displayExpr: "name",
                      searchEnabled: true,
                      placeholder: "Select approved vendor...",
                      onValueChanged: (e) => {
                        const vendor = allSuppliers.find(
                          (v) => v.id === e.value,
                        );
                        if (!vendor) return;

                        setFormData((prev) => ({
                          ...prev,
                          approvedVendorId: vendor.id,
                          vendorName: vendor.name,
                          vendorContact: vendor.contact,
                          vendorEmail: vendor.email,
                          vendorAddress: vendor.address,
                        }));
                      },
                    }}
                  >
                    <Label text="Approved Vendor" />
                  </Item>

                  <Item
                    dataField="vendorName"
                    //  visible={!formData.isAS9100}
                  />

                  <Item dataField="vendorContact">
                    <Label text="Vendor Phone Number" />
                  </Item>
                  <Item dataField="vendorEmail" />
                  <Item dataField="vendorAddress" />
                  {/*<Item dataField="fein" visible={false}>*/}
                  {/*    <Label text="FEIN" />*/}
                  {/*</Item>*/}
                  <Item
                    dataField="deadlineType"
                    editorType="dxSelectBox"
                    editorOptions={deadlineTypeOptions}
                  />
                  <Item
                    dataField="deadlineDate"
                    editorType="dxDateBox"
                    editorOptions={deadlineDateOptions}
                  >
                    <Label text="Desired Deadline Date" />
                  </Item>
                  <Item
                    dataField="urgencyJustification"
                    editorType="dxTextArea"
                  >
                    <Label text="Justification for Urgency" />
                  </Item>
                  <Item dataField="poAdditionalEmails">
                    <Label text="Contacts to CC" />
                  </Item>
                  <Item
                    dataField="additionalInfo"
                    editorType="dxTextArea"
                    colSpan={2}
                  >
                    <Label text="Description of Purchase" />
                  </Item>
                </Item>
                <Item
                  itemType="group"
                  caption="Required Information"
                  colCount={2}
                >
                  {/*<Item dataField="productDescription" editorType="dxTextArea" editorOptions={textAreaOptions} colSpan={2} visible={false}>*/}
                  {/*    <Label text="Provide a brief description of the product" />*/}
                  {/*</Item>*/}
                  {/*<Item dataField="productQuantity" editorType="dxNumberBox" visible={false}*/}
                  {/*    editorOptions={productQuantityOptions}>*/}
                  {/*    <Label text="Quantity" />*/}
                  {/*</Item>*/}
                  {/*<Item dataField="productCost" editorType="dxNumberBox" visible={false}*/}
                  {/*    editorOptions={productCostOptions}>*/}
                  {/*    <Label text="Price per Item" />*/}
                  {/*</Item>*/}
                  <Item
                    dataField="shippingLocation.id"
                    editorType="dxSelectBox"
                    editorOptions={locationsOptions}
                  >
                    <Label text="Delivery Location" />
                  </Item>
                  <Item dataField="shippingAttn">
                    <Label text="Shipping Attention" />
                  </Item>
                  <Item
                    dataField="provideShippingAccountOnPO"
                    editorType="dxCheckBox"
                    editorOptions={{
                      text: "Provide Shipping Acct on PO?",
                      elementAttr: {
                        class: "checkbox",
                      },
                    }}
                  >
                    <Label text={" "} />
                  </Item>

                  {/*<Item*/}
                  {/*    dataField="shippingRequirements"*/}
                  {/*    editorType="dxTextArea"*/}
                  {/*    editorOptions={textAreaOptions}*/}
                  {/*    visible={false}>*/}
                  {/*    <Label text="Are there any addditional shipping requirements?" />*/}
                  {/*</Item>*/}
                  {/*<Item*/}
                  {/*    dataField="relatedEquip"*/}
                  {/*    visible={false}>*/}
                  {/*    <Label text="Related Equipment (if applicable)" />*/}
                  {/*</Item>*/}
                  <Item
                    dataField="purchaseType"
                    editorType="dxSelectBox"
                    editorOptions={purchaseTypeOptions}
                  >
                    <Label text="Type of Purchase" />
                  </Item>
                  {/*<Item dataField="otherType" visible={false}>*/}
                  {/*    <Label text="Please Specify Type" />*/}
                  {/*</Item>*/}
                  <Item
                    dataField="isInstallationRequired"
                    editorType="dxCheckBox"
                    editorOptions={{
                      text: "Special Install Requirements?",
                      elementAttr: {
                        class: "checkbox",
                      },
                    }}
                  >
                    <Label text={" "} />
                  </Item>
                  <Item
                    dataField="installationRequirements"
                    editorType="dxTextArea"
                    editorOptions={textAreaOptions}
                  >
                    <Label text="Specify Requirements:" />
                  </Item>
                  <Item
                    dataField="purchaseTypeOptions"
                    editorType="dxSelectBox"
                    editorOptions={billingCodeOptions}
                  >
                    <Label text="ATLAS Billing Code" />
                  </Item>
                  <Item
                    dataField="total"
                    editorType="dxNumberBox"
                    editorOptions={totalOptions}
                  />
                  <Item dataField="otherBilling" colSpan={2}>
                    <Label text="Funding (if different than ATLAS)" />
                  </Item>

                  {/*Quick fix for editing status out of On Hold*/}
                  <Item
                    dataField="poStatus"
                    editorType="dxSelectBox"
                    editorOptions={poStatusOptions}
                    visible={false}
                  >
                    <Label text="Status" />
                  </Item>
                </Item>
              </Form>
            </Editing>

            <Column type="buttons" name="Actions">
              <Button
                text="Put On Hold"
                icon="warning"
                visible={/*isUserKristen && */ isRequestButtonVisible}
                onClick={(params) => {
                  setPopup("Put On Hold");
                  setSelectedPO(params.row.data);
                }}
              />
              <Button
                text="Remove Hold"
                icon="unlock"
                visible={/*isUserKristen && */ isRemoveButtonVisible}
                onClick={(params) => onRemoveHold(params.row.data)}
              />
              <Button
                text="View Quote"
                icon="doc"
                onClick={(params) => setQuoteId(params.row.data.id)}
              />
              <Button
                text="PO Details"
                icon="info"
                onClick={(params) =>
                  navigate(`/purchaseOrders/${params.row.data.id}`)
                }
              />
              <Button name="edit" visible={isButtonVisible} />
              <Button
                text="Cancel"
                icon="trash"
                visible={isButtonVisible}
                onClick={(params) => {
                  setPopup("Cancel Order");
                  setSelectedPO(params.row.data);
                }}
              />
            </Column>
            <Column
              dataField="id"
              dataType="number"
              caption="Request ID"
              allowHeaderFiltering={false}
              alignment="center"
            />
            <Column
              dataField="requestor.name"
              dataType="string"
              caption="Requestor"
            />
            <Column
              dataField="requestedDate"
              dataType="datetime"
              cellRender={handledDateRender}
              defaultSortOrder="desc"
            />
            <Column
              dataField="vendorName"
              dataType="string"
              caption="Vendor Name"
            />
            <Column
              dataField="deadlineDate"
              dataType="datetime"
              cellRender={handledDateRender}
            />
            <Column
              dataField="isAS9100"
              dataType="boolean"
              caption="AS9100 compliance"
              cellRender={(e) => booleanRender(e.data.isAS9100)}
            >
              <HeaderFilter dataSource={booleanTypes} />
            </Column>
            <Column
              dataField="deadlineType"
              dataType="number"
              caption="Urgency"
              cellRender={(e) => urgencyRender(e.data.deadlineType)}
            >
              <HeaderFilter dataSource={deadlineTypes} />
            </Column>
            <Column
              dataField="poStatus"
              dataType="number"
              caption="Status"
              cellRender={(e) => statusRender(e.data.poStatus)}
              // filterValues={filteredValues.current}
            >
              <HeaderFilter dataSource={statusValues} groupInterval={1} />
            </Column>
            {/*Hidden columns */}
            <Column
              dataField="vendorContact"
              dataType="string"
              visible={false}
            />
            <Column dataField="vendorEmail" dataType="string" visible={false} />
            <Column
              dataField="project.description"
              dataType="string"
              caption="Project Description"
              visible={false}
            />
            <Column
              dataField="vendorAddress"
              dataType="string"
              visible={false}
            />
            <Column
              dataField="urgencyJustification"
              dataType="string"
              visible={false}
            />
            <Column
              dataField="poAdditionalEmails"
              dataType="string"
              visible={false}
            />
            <Column
              dataField="additionalInfo"
              dataType="string"
              visible={false}
            />
            <Column
              dataField="shippingLocation.id"
              dataType="number"
              visible={false}
            />
            <Column
              dataField="shippingAttn"
              dataType="string"
              visible={false}
            />
            <Column
              dataField="provideShippingAccountOnPO"
              dataType="bool"
              visible={false}
            />
            <Column
              dataField="purchaseType"
              dataType="number"
              visible={false}
            />
            <Column
              dataField="isInstallationRequired"
              dataType="bool"
              visible={false}
            />
            <Column
              dataField="installationRequirements"
              dataType="string"
              visible={false}
            />
            <Column dataField="billingCode" dataType="string" visible={false} />
            <Column dataField="total" dataType="number" visible={false} />
            <Column
              dataField="otherBilling"
              dataType="string"
              visible={false}
            />
            <MasterDetail enabled={true} component={DetailTemplate} />
          </DataGrid>
        </>
      );
    }
  };

  if (
    !(data || error || locationData || locationError || userData || userError)
  ) {
    return <LoadingSpinnerModal show />;
  }

  return (
    <div className="px-lg-4">
      <Popup
        showPopup={showPopup}
        setPopup={setPopup}
        setSelectedPO={setSelectedPO}
        selectedPO={selectedPO}
        fetchSuccessful={fetchSuccessful}
        mutate={mutate}
        fetch={fetch}
      />
      <ActionNotifications response={notifyResponse.current} />
      <LoadingSpinnerModal show={showSpinner} />
      <div className="px-2 page-content">
        <div className="contentBox my-3">
          {!error ? renderGrid() : <ErrorMessage />}
        </div>
      </div>
    </div>
  );
}

const isRequestButtonVisible = (e) => {
  if (e.row.data.poStatus < 5 || e.row.data.poStatus !== 8) {
    return true;
  }
};
const isRemoveButtonVisible = (e) => {
  if (e.row.data.poStatus === 8) {
    return true;
  }
  return false;
};
const isButtonVisible = (e) => {
  const { poStatus } = e.row.data;

  if (poStatus === 7) return false;
  if (poStatus < 2 || ((poStatus < 4 || poStatus === 8) && checkRoles(6))) {
    return true;
  }
  return false;
};

const onRowPrepared = (e) => {
  if (e.rowType === "header") {
    e.rowElement.style.backgroundColor = "#FFCD00";
    e.rowElement.style.fontWeight = "bold";
  }
};
